import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://thecryptohub.com";
const DEFAULT_DESCRIPTION =
  "Discover cryptocurrency projects, Web3 news, blockchain events and community opportunities with The Crypto Hub.";

const PUBLIC_ROUTE_METADATA = {
  "/": {
    title: "The Crypto Hub | Crypto Projects, News & Events",
    description: DEFAULT_DESCRIPTION,
  },
  "/user/projects": {
    title: "Crypto & Web3 Projects | The Crypto Hub",
    description:
      "Discover cryptocurrency and Web3 projects, explore key details, and connect with emerging blockchain teams on The Crypto Hub.",
  },
  "/user/blogs": {
    title: "Crypto News & Web3 Insights | The Crypto Hub",
    description:
      "Read cryptocurrency news, Web3 insights, blockchain analysis and community stories from The Crypto Hub.",
  },
  "/user/events": {
    title: "Upcoming Crypto & Web3 Events | The Crypto Hub",
    description:
      "Discover upcoming cryptocurrency, blockchain and Web3 events, conferences and community gatherings with The Crypto Hub.",
  },
  "/user/past/events": {
    title: "Past Crypto & Web3 Events | The Crypto Hub",
    description:
      "Explore past cryptocurrency, blockchain and Web3 events featured by The Crypto Hub.",
  },
  "/user/join/crypto": {
    title: "Join The Crypto Hub Community",
    description:
      "Join The Crypto Hub community and connect with founders, investors, ambassadors and other Web3 professionals.",
  },
  "/user/contact": {
    title: "Contact The Crypto Hub",
    description:
      "Contact The Crypto Hub team about partnerships, projects, events, community opportunities and general enquiries.",
  },
  "/user/privacy/policy": {
    title: "Privacy Policy | The Crypto Hub",
    description:
      "Read The Crypto Hub privacy policy and learn how the website handles personal information and analytics choices.",
  },
  "/user/terms": {
    title: "Terms of Use | The Crypto Hub",
    description:
      "Read the terms of use governing access to The Crypto Hub website, newsletter, podcast, events and project listings.",
  },
  "/about": {
    title: "About The Crypto Hub",
    description:
      "Learn about The Crypto Hub, the marketing and fundraising platform connecting Blockchain, Crypto and Web3 projects with investors, mentors and KOLs.",
  },
};

const getPrivatePageTitle = (pathname) => {
  if (pathname.startsWith("/admin")) {
    return "Administration | The Crypto Hub";
  }

  if (
    pathname.startsWith("/user/login") ||
    pathname.startsWith("/user/signup") ||
    pathname.startsWith("/user/forgot") ||
    pathname.startsWith("/user/reset") ||
    pathname.startsWith("/user/verify")
  ) {
    return "Account | The Crypto Hub";
  }

  if (pathname.startsWith("/user/projectDetail")) {
    return "Project Details | The Crypto Hub";
  }

  if (pathname.startsWith("/user/blogDetail")) {
    return "Blog Details | The Crypto Hub";
  }

  if (pathname.startsWith("/user/eventDetails")) {
    return "Event Details | The Crypto Hub";
  }

  if (
    pathname.startsWith("/user/create") ||
    pathname.startsWith("/user/Add")
  ) {
    return "Create Content | The Crypto Hub";
  }

  return "Page Not Found | The Crypto Hub";
};

const upsertMeta = (attribute, value, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const setCanonical = (canonicalUrl) => {
  const canonicalElements = document.head.querySelectorAll(
    'link[rel="canonical"]'
  );

  if (!canonicalUrl) {
    canonicalElements.forEach((element) => element.remove());
    return;
  }

  let canonical = canonicalElements[0];

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  Array.from(canonicalElements)
    .slice(1)
    .forEach((element) => element.remove());
  canonical.setAttribute("href", canonicalUrl);
};

const RouteMetadata = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const normalizedPath =
      pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const publicMetadata = PUBLIC_ROUTE_METADATA[normalizedPath];
    const isIndexable = Boolean(publicMetadata);
    const title = publicMetadata?.title || getPrivatePageTitle(normalizedPath);
    const description = publicMetadata?.description || DEFAULT_DESCRIPTION;
    const pageUrl = `${SITE_URL}${normalizedPath}`;

    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta(
      "name",
      "robots",
      isIndexable ? "index, follow" : "noindex, nofollow"
    );
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", pageUrl);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:url", pageUrl);

    setCanonical(isIndexable ? pageUrl : null);
  }, [pathname]);

  return null;
};

export default RouteMetadata;
